import { useMemo, useRef } from 'react';

export type EventCreator<T, F extends (...args: any[]) => any> = (v: T) => F;

// This function takes an event handler creator function, and then
// returns a cached version of that handler. Given the same input,
// The event handler returned by that is always the same function,
// but when it is called, it runs the latest event. This is useful
// when we need to keep referential equality.
export const useEventCreator = <T, F extends (...args: any[]) => any>(
  eventCreator: EventCreator<T, F>,
): EventCreator<T, F> => {
  const events = useMemo(() => new Map<T, F>(), []);
  const creatorRef = useRef(eventCreator);
  creatorRef.current = eventCreator;

  return (v: T) => {
    if (!events.has(v)) {
      events.set(v, ((...args) => {
        return creatorRef.current(v)(...args);
      }) as F);
    }
    return events.get(v)!;
  };
};
